import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Modal,
  Select,
  Spinner,
  TextInput,
  Textarea,
} from "flowbite-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import moment from "moment";
import Chart from "react-apexcharts";
import {
  HiPencilAlt,
  HiOutlineTrash,
  HiUser,
  HiMail,
  HiPhone,
  HiGlobe,
  HiLocationMarker,
} from "react-icons/hi";
import axios from "../config/axiosInstance";
import { ADMIN, CRM, INTEGRATIONS, TEAMS, USERS } from "../config/apiConfig";
import CorporateBreadcrumb from "../components/common/CorporateBreadcrumb";

const PERMISSION_LABELS = {
  POWER_USER: "Power User",
  ADMIN: "Administrador",
  CLIENTE: "Cliente",
  REPRESENTANTE: "Representante",
};

const PERMISSION_OPTIONS = Object.entries(PERMISSION_LABELS).map(
  ([value, label]) => ({ value, label }),
);

const APP_ORIGIN = "https://app.relinqsales.com.br";

const COMPANY_INTEGRATION_FALLBACK = [
  { key: "whatsapp_meta", label: "WhatsApp Cloud API", scope: "company" },
  { key: "vonage_voice", label: "Vonage Voice", scope: "company" },
  { key: "openai", label: "OpenAI", scope: "company" },
  { key: "email_smtp", label: "E-mail SMTP", scope: "user" },
  { key: "google_calendar", label: "Google Calendar", scope: "user" },
  { key: "microsoft_calendar", label: "Microsoft Calendar", scope: "user" },
  { key: "meta_ads", label: "Meta Ads", scope: "company" },
  { key: "open_cnpj", label: "Open CNPJ", scope: "company" },
];

const INTEGRATION_CONFIG_SPECS = {
  whatsapp_meta: {
    title: "WhatsApp Cloud API",
    description: "Credenciais da API oficial da Meta para envio e recebimento.",
    fields: [
      { key: "whatsappAccessToken", label: "Access Token", type: "password" },
      { key: "whatsappPhoneNumberId", label: "Phone Number ID", type: "text" },
      {
        key: "whatsappBusinessAccountId",
        label: "Business Account ID",
        type: "text",
      },
      { key: "whatsappPhoneNumber", label: "Número do WhatsApp", type: "text" },
      {
        key: "whatsappWebhookVerifyToken",
        label: "Webhook Verify Token",
        type: "password",
      },
    ],
  },
  vonage_voice: {
    title: "Vonage Voice",
    description: "Credenciais para chamadas e telefonia via Vonage.",
    fields: [
      { key: "vonageApplicationId", label: "Application ID", type: "text" },
      { key: "vonagePrivateKey", label: "Private Key", type: "textarea" },
      { key: "vonagePhoneNumber", label: "Número Vonage", type: "text" },
    ],
  },
  openai: {
    title: "OpenAI",
    description: "Chave e prompt base usados em recursos de IA.",
    fields: [
      { key: "openaiApiKey", label: "API Key", type: "password" },
      { key: "voipAiSalesPrompt", label: "Prompt de vendas (VoIP)", type: "textarea" },
    ],
  },
};

const INTEGRATION_CONFIG_NOTES = {
  email_smtp:
    "Integração por usuário. O cadastro é feito no ambiente autenticado do usuário final.",
  google_calendar:
    "Integração por usuário. A conexão é feita via OAuth no perfil do usuário.",
  microsoft_calendar:
    "Integração por usuário. A conexão é feita via OAuth no perfil do usuário.",
  meta_ads:
    "Configuração por funil (formulário, token e mapeamentos).",
  open_cnpj: "Não exige variáveis de configuração por empresa.",
};

const formatCurrency = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = moment(value);
  if (!date.isValid()) return "—";
  return date.format("DD/MM/YYYY");
};

const badgeColorFromStatus = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized.includes("trial") || normalized.includes("teste"))
    return "warning";
  if (normalized.includes("cancel") || normalized.includes("expir"))
    return "failure";
  return "success";
};

const trendLabelFormatter = new Intl.DateTimeFormat("pt-BR", {
  month: "short",
  year: "numeric",
});

const BASE_TREND_OPTIONS = {
  chart: {
    toolbar: { show: false },
    zoom: { enabled: false },
    background: "transparent",
    foreColor: "#475569",
  },
  stroke: { curve: "smooth", width: 3 },
  markers: { size: 4, strokeWidth: 2 },
  grid: { borderColor: "rgba(148, 163, 184, 0.3)", strokeDashArray: 6 },
  dataLabels: { enabled: false },
  tooltip: { theme: "dark" },
  fill: {
    type: "gradient",
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.8,
      opacityTo: 0.2,
      stops: [0, 80],
      gradientToColors: ["#22c55e"],
    },
  },
  yaxis: {
    labels: {
      formatter: (value) =>
        typeof value === "number"
          ? value.toLocaleString("pt-BR", { maximumFractionDigits: 0 })
          : value,
    },
  },
  xaxis: {
    labels: {
      style: { colors: "#94a3b8", fontSize: "12px", fontWeight: 500 },
    },
  },
};

const parseMonthKey = (value) => {
  const [year, month] = String(value || "")
    .split("-")
    .map((part) => +part);
  if (!year || !month) return new Date();
  return new Date(year, month - 1, 1);
};

const buildTrendChartData = (rows = []) => {
  const categories = [];
  const values = [];
  rows.forEach((entry) => {
    const monthKey = entry?.month || "";
    categories.push(trendLabelFormatter.format(parseMonthKey(monthKey)));
    values.push(Number(entry?.count) || 0);
  });
  return { categories, values };
};

const buildChartOptions = (categories = [], primaryColor, accentColor) => ({
  ...BASE_TREND_OPTIONS,
  colors: [primaryColor],
  fill: {
    ...BASE_TREND_OPTIONS.fill,
    gradient: {
      ...BASE_TREND_OPTIONS.fill.gradient,
      gradientToColors: [accentColor],
    },
  },
  xaxis: { ...BASE_TREND_OPTIONS.xaxis, categories },
});

const formatDateForInput = (value) => {
  if (!value) return "";
  return moment(value).format("YYYY-MM-DD");
};

const parseToInt = (value) => {
  const parsed = parseInt(String(value || ""), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const buildDefaultIntegrationMap = (list = COMPANY_INTEGRATION_FALLBACK) =>
  list.reduce((acc, integration) => {
    acc[integration.key] = true;
    return acc;
  }, {});

const EDITABLE_FIELDS = [
  { key: "name", label: "Nome", placeholder: "Nome fantasia" },
  { key: "socialReason", label: "Razão social", placeholder: "Razão social" },
  { key: "document", label: "Documento", placeholder: "CNPJ / CPF" },
  { key: "email", label: "E-mail", placeholder: "Contato principal" },
  { key: "phoneNumber", label: "Telefone", placeholder: "(00) 00000-0000" },
  { key: "address", label: "Endereço", placeholder: "Rua, número etc." },
  { key: "addressNumber", label: "Número", placeholder: "Número" },
  { key: "addressNeighborhood", label: "Bairro", placeholder: "Bairro" },
  { key: "addressCity", label: "Cidade", placeholder: "Cidade" },
  { key: "zipCode", label: "CEP", placeholder: "00000-000", targetKey: "cep" },
  { key: "website", label: "Website", placeholder: "https://..." },
  {
    key: "responsibleName",
    label: "Responsável",
    placeholder: "Responsável principal",
  },
  {
    key: "leadGeneratorMonthlyLimit",
    label: "Limite de leads/mês",
    placeholder: "Ex.: 100",
  },
  {
    key: "budgetLimit",
    label: "Limite de orçamentos/mês",
    placeholder: "Ex.: 20",
  },
  { key: "funnelLimit", label: "Limite de funis", placeholder: "Ex.: 5" },
];

const CompanyDetailPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery(
    ["company-detail", companyId],
    async () => {
      const response = await axios.get(ADMIN.GET_COMPANY_DETAIL(companyId));
      return response.data?.data || null;
    },
    { enabled: Boolean(companyId), staleTime: 5 * 60 * 1000 },
  );

  const {
    data: integrationAccessData,
    isLoading: isLoadingIntegrationAccess,
    error: integrationAccessError,
    refetch: refetchIntegrationAccess,
  } = useQuery(
    ["company-integration-access", companyId],
    async () => {
      const [currentResult, matrixResult] = await Promise.allSettled([
        axios.get(INTEGRATIONS.COMPANY_ACCESS_CURRENT),
        axios.get(INTEGRATIONS.COMPANY_ACCESS_MATRIX),
      ]);

      let integrations = [...COMPANY_INTEGRATION_FALLBACK];
      let accessMap = buildDefaultIntegrationMap(integrations);

      if (currentResult.status === "fulfilled") {
        const currentMap = currentResult.value?.data?.data?.integrations;
        if (currentMap && typeof currentMap === "object") {
          accessMap = { ...accessMap, ...currentMap };
        }
      }

      if (matrixResult.status === "fulfilled") {
        const matrixPayload = matrixResult.value?.data?.data || {};
        const listFromApi = Array.isArray(matrixPayload.integrations)
          ? matrixPayload.integrations
          : [];
        if (listFromApi.length) {
          integrations = listFromApi;
          accessMap = {
            ...buildDefaultIntegrationMap(integrations),
            ...accessMap,
          };
        }

        const currentCompanyId = parseToInt(companyId);
        const matrixRows = Array.isArray(matrixPayload.matrix)
          ? matrixPayload.matrix
          : [];
        const row = matrixRows.find(
          (item) => parseToInt(item?.companyId) === currentCompanyId,
        );
        if (row?.integrations && typeof row.integrations === "object") {
          accessMap = { ...accessMap, ...row.integrations };
        }
      }

      return { integrations, accessMap };
    },
    {
      enabled: Boolean(companyId),
      staleTime: 60 * 1000,
    },
  );

  const company = data?.company;
  const subscription = data?.subscription;
  const charges = data?.charges || [];
  const teams = data?.teams || [];
  const funnels = data?.funnels || [];
  const users = data?.users || [];
  const filiais = data?.filiais || [];
  const planName =
    subscription?.company?.plan?.name ||
    company?.plan?.name ||
    company?.plan?.code ||
    "Plano não informado";
  const companyName = company?.socialReason || company?.name || "Empresa";
  const planCycleLabel =
    subscription?.planCycle?.description ||
    (subscription?.planCycle?.intervalMonths
      ? `${subscription.planCycle.intervalMonths} meses`
      : "-");
  const planExpiration =
    subscription?.planExpirationDate || company?.planExpirationDate;
  const planStatus = subscription?.planStatus || "Ativo";
  const isTrial = !company?.planId || !company?.planCycleId;

  const addressParts = [
    company?.address,
    company?.logradouro,
    company?.street,
    company?.tipo_logradouro,
    company?.numero || company?.addressNumber,
    company?.bairro,
  ]
    .filter(Boolean)
    .join(", ");
  const cityState = [
    company?.municipio || company?.city,
    company?.uf || company?.state,
  ]
    .filter(Boolean)
    .join(" / ");
  const locationLabel = [addressParts, cityState].filter(Boolean).join(" • ");

  const detailRows = [
    { key: "name", label: "Nome", value: company?.name },
    {
      key: "socialReason",
      label: "Razão social",
      value: company?.socialReason,
    },
    {
      key: "document",
      label: "Documento",
      value:
        company?.document ||
        company?.cpf ||
        company?.cnpj ||
        company?.documentId ||
        "—",
    },
    {
      key: "segment",
      label: "Segmento",
      value:
        company?.segmentName ||
        company?.segment ||
        company?.segment?.name ||
        company?.businessArea ||
        company?.industry,
    },
    {
      key: "cnae",
      label: "CNAE",
      value:
        company?.cnae ||
        company?.cnaePrincipal ||
        company?.cnae_principal ||
        company?.mainCnae,
    },
    {
      key: "website",
      label: "Website",
      value: company?.website || company?.site || company?.websiteUrl,
    },
    { key: "address", label: "Endereço", value: locationLabel },
    {
      key: "zipCode",
      label: "CEP",
      value: company?.zipCode || company?.postalCode || company?.cep,
    },
    { key: "phoneNumber", label: "Telefone", value: company?.phoneNumber },
    { key: "email", label: "E-mail", value: company?.email },
    {
      key: "responsibleName",
      label: "Responsável",
      value:
        company?.responsibleName ||
        company?.responsible ||
        company?.representative ||
        company?.ownerName ||
        company?.contactName,
    },
    {
      key: "leadGeneratorMonthlyLimit",
      label: "Limite de leads/mês",
      value:
        company?.leadGeneratorMonthlyLimit !== undefined &&
        company?.leadGeneratorMonthlyLimit !== null
          ? company.leadGeneratorMonthlyLimit
          : "—",
    },
    {
      key: "budgetLimit",
      label: "Limite de orçamentos/mês",
      value: company?.budgetLimit ?? "-",
    },
    {
      key: "funnelLimit",
      label: "Limite de funis",
      value: company?.funnelLimit ?? "-",
    },
    {
      key: "createdAt",
      label: "Criada em",
      value:
        formatDate(
          company?.createdAt || company?.created_at || company?.creationDate,
        ) || "—",
    },
    {
      key: "billingPortalUrl",
      label: "Portal de cobrança",
      value: company?.billingPortalUrl ? (
        <a
          href={company.billingPortalUrl}
          rel="noreferrer"
          target="_blank"
          className="text-orange-600 underline"
        >
          Acessar portal
        </a>
      ) : null,
    },
  ].filter(
    (row) =>
      row.value !== undefined &&
      row.value !== null &&
      row.value !== "" &&
      row.value !== "—",
  );

  const editableFieldKeys = new Set(EDITABLE_FIELDS.map((field) => field.key));
  const [companyForm, setCompanyForm] = useState({});
  const [companyEditModal, setCompanyEditModal] = useState({
    open: false,
    focusField: null,
    loading: false,
    error: "",
  });

  useEffect(() => {
    if (!company) return;
    const initialValues = EDITABLE_FIELDS.reduce((acc, field) => {
      let value = company[field.key];
      if (value === undefined || value === null || value === "") {
        if (field.key === "zipCode") {
          value = company?.zipCode || company?.cep || company?.postalCode || "";
        } else if (field.key === "addressCity") {
          value = company?.addressCity || company?.city || "";
        } else if (field.key === "responsibleName") {
          value =
            company?.responsibleName ||
            company?.responsible ||
            company?.contactName ||
            "";
        } else if (field.key === "socialReason") {
          value = company?.socialReason || "";
        } else if (field.key === "name") {
          value = company?.name || "";
        }
      }
      acc[field.key] = value ?? "";
      return acc;
    }, {});
    initialValues.planId = company?.planId ? String(company.planId) : "";
    initialValues.planCycleId = company?.planCycleId
      ? String(company.planCycleId)
      : "";
    initialValues.planExpirationDate = formatDateForInput(
      company?.planExpirationDate,
    );
    initialValues.isTrial = !company?.planId || !company?.planCycleId;
    setCompanyForm(initialValues);
  }, [company]);

  const planOptions = subscription?.availablePlans || [];
  const selectedPlan = planOptions.find(
    (plan) => String(plan.id) === String(companyForm.planId),
  );
  const planCycles = selectedPlan?.cycles || [];

  const handleCompanyFormChange = (key, value) => {
    setCompanyForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleTrialToggle = (checked) => {
    if (checked) {
      setCompanyForm((prev) => ({
        ...prev,
        isTrial: true,
        planId: "",
        planCycleId: "",
      }));
      return;
    }
    const fallbackPlan = planOptions[0];
    const fallbackCycleId = fallbackPlan?.cycles?.[0]?.id
      ? String(fallbackPlan.cycles[0].id)
      : "";
    setCompanyForm((prev) => ({
      ...prev,
      isTrial: false,
      planId: fallbackPlan ? String(fallbackPlan.id) : "",
      planCycleId: fallbackCycleId,
    }));
  };

  const handlePlanChange = (planId) => {
    const plan = planOptions.find((option) => String(option.id) === planId);
    const cycleId = plan?.cycles?.[0]?.id
      ? String(plan.cycles[0].id)
      : companyForm.planCycleId;
    setCompanyForm((prev) => ({
      ...prev,
      planId: planId || "",
      planCycleId: cycleId || "",
    }));
  };

  const handleCycleChange = (cycleId) => {
    setCompanyForm((prev) => ({ ...prev, planCycleId: cycleId || "" }));
  };

  const handleOpenCompanyEditModal = (fieldKey) => {
    setCompanyEditModal({
      open: true,
      focusField: fieldKey,
      loading: false,
      error: "",
    });
  };

  const closeCompanyEditModal = () =>
    setCompanyEditModal((prev) => ({
      ...prev,
      open: false,
      focusField: null,
      loading: false,
      error: "",
    }));

  const handleSaveCompanyInfo = async () => {
    if (!companyId) return;
    setCompanyEditModal((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const numericLimitFields = new Set([
        "leadGeneratorMonthlyLimit",
        "budgetLimit",
        "funnelLimit",
      ]);
      const payload = EDITABLE_FIELDS.reduce((acc, field) => {
        if (field.key in companyForm) {
          const targetKey = field.targetKey || field.key;
          const rawValue = companyForm[field.key];
          if (numericLimitFields.has(targetKey)) {
            if (
              rawValue === "" ||
              rawValue === null ||
              typeof rawValue === "undefined"
            ) {
              acc[targetKey] = null;
            } else {
              const parsed = Number(rawValue);
              acc[targetKey] = Number.isFinite(parsed) ? parsed : rawValue;
            }
          } else {
            acc[targetKey] = rawValue;
          }
        }
        return acc;
      }, {});
      const payingTrial = Boolean(companyForm.isTrial);
      const planPayload = {
        planId:
          payingTrial || !companyForm.planId
            ? null
            : Number(companyForm.planId),
        planCycleId:
          payingTrial || !companyForm.planCycleId
            ? null
            : Number(companyForm.planCycleId),
        planExpirationDate: companyForm.planExpirationDate || null,
      };
      await axios.put(ADMIN.UPDATE_COMPANY(companyId), {
        ...payload,
        ...planPayload,
      });
      refreshCompany();
      closeCompanyEditModal();
    } catch (err) {
      console.error(err);
      setCompanyEditModal((prev) => ({
        ...prev,
        loading: false,
        error:
          err?.response?.data?.message ||
          "Não foi possível atualizar os dados no momento.",
      }));
    }
  };

  const leadsByMonth = data?.leadsByMonth || [];
  const budgetsByMonth = data?.budgetsByMonth || [];
  const trendMonths = data?.trendMonths || 6;

  const leadTrendData = useMemo(
    () => buildTrendChartData(leadsByMonth),
    [leadsByMonth],
  );
  const budgetTrendData = useMemo(
    () => buildTrendChartData(budgetsByMonth),
    [budgetsByMonth],
  );

  const leadChartOptions = useMemo(
    () => buildChartOptions(leadTrendData.categories, "#059669", "#22c55e"),
    [leadTrendData.categories],
  );
  const budgetChartOptions = useMemo(
    () => buildChartOptions(budgetTrendData.categories, "#2563eb", "#60a5fa"),
    [budgetTrendData.categories],
  );

  const totalLeads = leadsByMonth.reduce(
    (sum, row) => sum + (Number(row?.count) || 0),
    0,
  );
  const totalBudgets = budgetsByMonth.reduce(
    (sum, row) => sum + (Number(row?.count) || 0),
    0,
  );
  const latestLeadCount = leadsByMonth.at(-1)?.count ?? 0;
  const latestBudgetCount = budgetsByMonth.at(-1)?.count ?? 0;

  const [teamMembersModal, setTeamMembersModal] = useState({
    isOpen: false,
    loading: false,
    members: [],
    team: null,
    error: "",
  });
  const [teamEditModal, setTeamEditModal] = useState({
    open: false,
    team: null,
    form: { name: "", description: "" },
    loading: false,
    error: "",
  });
  const [teamDeleteModal, setTeamDeleteModal] = useState({
    open: false,
    team: null,
    loading: false,
  });
  const [userEditModal, setUserEditModal] = useState({
    open: false,
    user: null,
    form: { name: "", email: "", permission: "" },
    loading: false,
    error: "",
  });
  const [userDeleteModal, setUserDeleteModal] = useState({
    open: false,
    user: null,
    loading: false,
  });
  const [activeTab, setActiveTab] = useState("overview");
  const [impersonateState, setImpersonateState] = useState({
    loadingId: null,
    error: "",
  });
  const [funnelModal, setFunnelModal] = useState({
    open: false,
    funnel: null,
    detail: null,
    loading: false,
    error: "",
  });
  const [integrationAccessState, setIntegrationAccessState] = useState({
    integrations: COMPANY_INTEGRATION_FALLBACK,
    accessMap: buildDefaultIntegrationMap(),
    savingKey: "",
    error: "",
  });
  const [integrationConfigState, setIntegrationConfigState] = useState({
    form: {},
    savingIntegrationKey: "",
    error: "",
    success: "",
  });

  useEffect(() => {
    if (!integrationAccessData) return;
    setIntegrationAccessState((prev) => ({
      ...prev,
      integrations:
        integrationAccessData.integrations || COMPANY_INTEGRATION_FALLBACK,
      accessMap:
        integrationAccessData.accessMap ||
        buildDefaultIntegrationMap(integrationAccessData.integrations),
      error: "",
    }));
  }, [integrationAccessData]);

  useEffect(() => {
    if (!company) return;

    const initialForm = {};
    Object.values(INTEGRATION_CONFIG_SPECS).forEach((spec) => {
      spec.fields.forEach((field) => {
        initialForm[field.key] = company?.[field.key] ?? "";
      });
    });

    setIntegrationConfigState((prev) => ({
      ...prev,
      form: initialForm,
      error: "",
      success: "",
    }));
  }, [company]);

  const refreshCompany = () => {
    if (companyId) queryClient.invalidateQueries(["company-detail", companyId]);
  };

  const handleIntegrationToggle = async (integrationKey, isEnabled) => {
    const parsedCompanyId = parseToInt(companyId);
    if (!parsedCompanyId || !integrationKey) return;

    const previousValue = Boolean(
      integrationAccessState.accessMap?.[integrationKey],
    );

    setIntegrationAccessState((prev) => ({
      ...prev,
      accessMap: { ...prev.accessMap, [integrationKey]: isEnabled },
      savingKey: integrationKey,
      error: "",
    }));

    try {
      await axios.put(INTEGRATIONS.COMPANY_ACCESS_SET, {
        companyId: parsedCompanyId,
        integrationKey,
        isEnabled,
      });
      await refetchIntegrationAccess();
    } catch (err) {
      setIntegrationAccessState((prev) => ({
        ...prev,
        accessMap: { ...prev.accessMap, [integrationKey]: previousValue },
        error:
          err?.response?.data?.message ||
          "Não foi possível salvar a permissão da integração.",
      }));
    } finally {
      setIntegrationAccessState((prev) => ({
        ...prev,
        savingKey: "",
      }));
    }
  };

  const handleIntegrationConfigChange = (fieldKey, value) => {
    setIntegrationConfigState((prev) => ({
      ...prev,
      form: { ...prev.form, [fieldKey]: value },
      error: "",
      success: "",
    }));
  };

  const handleSaveIntegrationConfig = async (integrationKey) => {
    const parsedCompanyId = parseToInt(companyId);
    const spec = INTEGRATION_CONFIG_SPECS[integrationKey];
    if (!parsedCompanyId || !spec) return;

    const payload = spec.fields.reduce((acc, field) => {
      const raw = integrationConfigState.form?.[field.key];
      const value = typeof raw === "string" ? raw.trim() : raw;
      acc[field.key] = value === "" ? null : value;
      return acc;
    }, {});

    setIntegrationConfigState((prev) => ({
      ...prev,
      savingIntegrationKey: integrationKey,
      error: "",
      success: "",
    }));

    try {
      await axios.put(ADMIN.UPDATE_COMPANY(parsedCompanyId), payload);
      refreshCompany();
      setIntegrationConfigState((prev) => ({
        ...prev,
        success: `Configuração de ${spec.title} salva com sucesso.`,
      }));
    } catch (err) {
      setIntegrationConfigState((prev) => ({
        ...prev,
        error:
          err?.response?.data?.message ||
          `Não foi possível salvar a configuração de ${spec.title}.`,
      }));
    } finally {
      setIntegrationConfigState((prev) => ({
        ...prev,
        savingIntegrationKey: "",
      }));
    }
  };

  const handleShowTeamMembers = async (team) => {
    if (!team?.id) return;
    setTeamMembersModal({
      isOpen: true,
      loading: true,
      members: [],
      team,
      error: "",
    });
    try {
      const response = await axios.get(TEAMS.LIST_USERS(team.id));
      const list = response?.data?.data || response?.data || [];
      setTeamMembersModal((prev) => ({
        ...prev,
        loading: false,
        members: Array.isArray(list) ? list : [],
      }));
    } catch {
      setTeamMembersModal((prev) => ({
        ...prev,
        loading: false,
        error: "Não foi possível carregar a lista de membros.",
      }));
    }
  };

  const closeTeamMembers = () =>
    setTeamMembersModal((prev) => ({ ...prev, isOpen: false }));

  const openTeamEditModal = (team) => {
    setTeamEditModal({
      open: true,
      team,
      form: { name: team?.name || "", description: team?.description || "" },
      loading: false,
      error: "",
    });
  };

  const closeTeamEditModal = () =>
    setTeamEditModal((prev) => ({ ...prev, open: false }));

  const saveTeamChanges = async () => {
    const { team, form } = teamEditModal;
    if (!team?.id) return;
    setTeamEditModal((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      await axios.put(TEAMS.UPDATE(team.id), {
        name: form.name,
        description: form.description,
      });
      refreshCompany();
      closeTeamEditModal();
    } catch {
      setTeamEditModal((prev) => ({
        ...prev,
        loading: false,
        error: "Não foi possível salvar as alterações.",
      }));
    }
  };

  const openTeamDeleteModal = (team) => {
    setTeamDeleteModal({ open: true, team, loading: false });
  };

  const closeTeamDeleteModal = () =>
    setTeamDeleteModal((prev) => ({ ...prev, open: false }));

  const handleDeleteTeam = async () => {
    const { team } = teamDeleteModal;
    if (!team?.id) return;
    setTeamDeleteModal((prev) => ({ ...prev, loading: true }));
    try {
      await axios.delete(TEAMS.DELETE(team.id));
      refreshCompany();
      closeTeamDeleteModal();
    } catch (err) {
      console.error(err);
      setTeamDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const openUserEditModal = (user) => {
    setUserEditModal({
      open: true,
      user,
      form: {
        name: user?.name || "",
        email: user?.email || "",
        permission: user?.permissionId || "",
      },
      loading: false,
      error: "",
    });
  };

  const closeUserEditModal = () =>
    setUserEditModal((prev) => ({ ...prev, open: false }));

  const saveUserChanges = async () => {
    const { user, form } = userEditModal;
    if (!user?.id) return;
    setUserEditModal((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      await axios.put(`${USERS.GET_USER}/${user.id}`, {
        name: form.name,
        email: form.email,
        permissionId: form.permission,
      });
      refreshCompany();
      closeUserEditModal();
    } catch {
      setUserEditModal((prev) => ({
        ...prev,
        loading: false,
        error: "Não foi possível atualizar as informações do usuário.",
      }));
    }
  };

  const openUserDeleteModal = (user) => {
    setUserDeleteModal({ open: true, user, loading: false });
  };

  const closeUserDeleteModal = () =>
    setUserDeleteModal((prev) => ({ ...prev, open: false }));

  const handleDeleteUser = async () => {
    const { user } = userDeleteModal;
    if (!user?.id) return;
    setUserDeleteModal((prev) => ({ ...prev, loading: true }));
    try {
      await axios.delete(`${USERS.GET_USER}/${user.id}`);
      refreshCompany();
      closeUserDeleteModal();
    } catch (err) {
      console.error(err);
      setUserDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleImpersonateUser = async (user) => {
    if (!user?.id) return;
    setImpersonateState({ loadingId: user.id, error: "" });
    try {
      const response = await axios.post(ADMIN.IMPERSONATE_USER(user.id));
      const rawToken =
        response?.data?.token ??
        response?.data?.data?.token ??
        response?.data?.data ??
        response?.data;
      const token = String(rawToken || "")
        .replace(/^Bearer\s+/i, "")
        .trim();
      if (!token) throw new Error("Token ausente na resposta.");
      const targetUrl = `${APP_ORIGIN}/impersonate?token=${encodeURIComponent(token)}`;
      const popup = window.open(targetUrl, "_blank", "noopener,noreferrer");
      if (!popup)
        throw new Error(
          "Não foi possível abrir a nova aba. Verifique o bloqueador de pop-ups.",
        );
      setImpersonateState({ loadingId: null, error: "" });
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Não foi possível gerar o token de acesso.";
      setImpersonateState({ loadingId: null, error: message });
    }
  };

  const handleShowFunnelDetails = async (funnel) => {
    const funnelId = funnel?.id || funnel?.funnelId;
    if (!funnelId) return;
    setFunnelModal({
      open: true,
      funnel,
      detail: null,
      automations: [],
      loading: true,
      error: "",
    });
    try {
      const [detailResponse, automationResponse] = await Promise.all([
        axios.get(`${CRM.FUNNEL_DETAILS}/${funnelId}/details`),
        axios.get(CRM.AUTOMATIONS_LIST(funnelId)),
      ]);
      const detailData =
        detailResponse?.data?.data || detailResponse?.data || null;
      const normalizedStages = (detailData?.stages || []).map((stage) => {
        const cards = Array.isArray(stage.cards) ? stage.cards : [];
        const sorted = [...cards].sort((a, b) => {
          const pa = Number.isFinite(+a.priority) ? +a.priority : Infinity;
          const pb = Number.isFinite(+b.priority) ? +b.priority : Infinity;
          return pa - pb;
        });
        return {
          ...stage,
          cards: sorted.map((card, index) => ({
            ...card,
            priority: Number.isFinite(+card.priority)
              ? +card.priority
              : index + 1,
          })),
        };
      });
      const normalizedDetail = detailData
        ? { ...detailData, stages: normalizedStages }
        : null;
      const automationsRaw = automationResponse?.data?.data;
      const automationsFallback = Array.isArray(automationsRaw)
        ? automationsRaw
        : Array.isArray(automationResponse?.data)
          ? automationResponse.data
          : [];
      setFunnelModal((prev) => ({
        ...prev,
        detail: normalizedDetail,
        automations: automationsFallback,
        loading: false,
      }));
    } catch (err) {
      console.error("Erro ao carregar detalhes do funil:", err);
      setFunnelModal((prev) => ({
        ...prev,
        automations: [],
        loading: false,
        error: "Não foi possível carregar os detalhes do funil.",
      }));
    }
  };

  const closeFunnelModal = () =>
    setFunnelModal((prev) => ({ ...prev, open: false }));

  const funnelDetail = funnelModal.detail;
  const stageList = Array.isArray(funnelDetail?.stages)
    ? funnelDetail.stages
    : [];
  const stageLookup = new Map(
    stageList.map((stage) => [String(stage.id), stage]),
  );
  const stageSummaries = stageList.map((stage, index) => {
    const cardsCount =
      Array.isArray(stage.cards) && stage.cards.length
        ? stage.cards.length
        : Number.isFinite(+stage.cardsCount)
          ? +stage.cardsCount
          : Number.isFinite(+stage.cardsTotal)
            ? +stage.cardsTotal
            : 0;
    const automationCount = Array.isArray(stage.automations)
      ? stage.automations.length
      : Number.isFinite(+stage.automationCount)
        ? +stage.automationCount
        : 0;
    return {
      id: stage.id || `stage-${index}`,
      name: stage.name || stage.title || `Etapa ${index + 1}`,
      status: stage.status || stage.visibility || "—",
      cardsCount,
      automationCount,
      order:
        Number.isFinite(+stage.order) || Number.isFinite(+stage.position)
          ? +stage.order || +stage.position
          : index + 1,
      responsible:
        stage.team?.name ||
        stage.responsible?.name ||
        stage.owner?.name ||
        stage.personnel?.name ||
        null,
    };
  });
  const totalCardsInFunnel = stageSummaries.reduce(
    (sum, stage) => sum + stage.cardsCount,
    0,
  );
  const totalAutomations = (funnelModal.automations || []).length;
  const funnelVisibility =
    funnelDetail?.visibility ||
    funnelDetail?.visibilityName ||
    funnelDetail?.publicity ||
    "Pública";
  const funnelOwner =
    funnelDetail?.teamName ||
    funnelDetail?.team?.name ||
    funnelDetail?.ownerName ||
    funnelDetail?.owner?.name ||
    funnelDetail?.leader?.name ||
    null;

  const describeTriggerSummary = (automation = {}) => {
    const triggers = Array.isArray(automation.triggers)
      ? automation.triggers
      : automation.trigger
        ? [automation.trigger]
        : [];
    if (!triggers.length) {
      return (
        automation.triggerEvent ||
        automation.event ||
        automation.triggerName ||
        ""
      );
    }
    return triggers
      .map((trigger) => {
        const stageId = trigger.stageId || trigger.stage?.id;
        const stage =
          stageLookup.get(String(stageId)) ||
          stageLookup.get(String(trigger.stage?.id));
        const stageName =
          stage?.name || trigger.stage?.name || trigger.stageName || null;
        const eventLabel = trigger.event || trigger.type || "Evento";
        return [eventLabel, stageName].filter(Boolean).join(" • ");
      })
      .filter(Boolean)
      .join(" | ");
  };

  const describeActionsSummary = (automation = {}) => {
    const actions = Array.isArray(automation.actions)
      ? automation.actions
      : automation.action
        ? [automation.action]
        : [];
    if (!actions.length) return "";
    return actions
      .map(
        (action) =>
          action.name ||
          action.type ||
          action.label ||
          action.title ||
          "Ação personalizada",
      )
      .filter(Boolean)
      .join(" • ");
  };

  const tabs = useMemo(
    () => [
      { id: "overview", label: "Visão geral" },
      {
        id: "integrations",
        label: "Integrações",
        count: integrationAccessState.integrations.length,
      },
      { id: "indicators", label: "Indicadores" },
      { id: "users", label: "Usuários", count: users.length },
      { id: "funnels", label: "Funis", count: funnels.length },
      { id: "teams", label: "Times", count: teams.length },
      { id: "filiais", label: "Filiais", count: filiais.length },
      { id: "payments", label: "Pagamentos", count: charges.length },
    ],
    [
      charges.length,
      filiais.length,
      funnels.length,
      teams.length,
      users.length,
      integrationAccessState.integrations.length,
    ],
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100">
            <Spinner size="lg" color="warning" />
          </div>
          <p className="text-sm font-medium text-slate-500">
            Carregando empresa...
          </p>
        </div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="space-y-4">
        <Alert color="failure">
          <span className="font-semibold">
            Não foi possível carregar os detalhes.
          </span>{" "}
          Tente novamente mais tarde.
        </Alert>
        <Link to="/companies">
          <Button color="gray">Voltar para empresas</Button>
        </Link>
      </div>
    );
  }

  const planStatusIsNegative =
    planStatus.toLowerCase().includes("cancel") ||
    planStatus.toLowerCase().includes("expir");

  return (
    <div className="space-y-4">
      <CorporateBreadcrumb
        items={[
          { label: "Empresas", href: "/companies" },
          { label: companyName, current: true },
        ]}
      />

      {/* ── Hero card ── */}
      <div className="relative overflow-hidden rounded-2xl bg-white shadow-sm">
        <div className="relative px-6 py-6 lg:px-8 lg:py-7">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            {/* Identity */}
            <div className="flex items-start gap-4">
              <div className="min-w-0">
                <p className="text-[10px] font-medium uppercase tracking-widest text-orange-200">
                  Empresa · ID {company?.id ?? "—"}
                </p>
                <h1 className="mt-0.5 text-2xl font-bold leading-tight ">
                  {company.socialReason || company.name}
                </h1>
                {company.name &&
                  company.socialReason &&
                  company.name !== company.socialReason && (
                    <p className="text-sm text-orange-200">{company.name}</p>
                  )}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${
                      isTrial
                        ? "bg-amber-400/25 text-amber-100 ring-amber-300/30"
                        : planStatusIsNegative
                          ? "bg-red-400/25 text-red-100 ring-red-300/30"
                          : "bg-emerald-400/25 text-emerald-100 ring-emerald-300/30"
                    }`}
                  >
                    {isTrial ? "Período de teste" : planStatus}
                  </span>
                  <span className="text-xs text-orange-200">{planName}</span>
                  {planExpiration && (
                    <span className="text-xs text-orange-200">
                      · Expira {formatDate(planExpiration)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Stat pills */}
            <div className="flex flex-wrap items-center gap-2 lg:shrink-0">
              {[
                {
                  label: "Funis",
                  value: funnels.length,
                  tone: "from-sky-500/35 to-blue-500/10 border-sky-200/45 text-sky-50",
                  labelTone: "text-sky-100/90",
                },
                {
                  label: "Times",
                  value: teams.length,
                  tone: "from-emerald-500/35 to-green-500/10 border-emerald-200/45 text-emerald-50",
                  labelTone: "text-emerald-100/90",
                },
                {
                  label: "Usuários",
                  value: users.length,
                  tone: "from-violet-500/35 to-purple-500/10 border-violet-200/45 text-violet-50",
                  labelTone: "text-violet-100/90",
                },
                {
                  label: "Filiais",
                  value: filiais.length,
                  tone: "from-fuchsia-500/35 to-pink-500/10 border-fuchsia-200/45 text-fuchsia-50",
                  labelTone: "text-fuchsia-100/90",
                },
                {
                  label: "Cobranças",
                  value: charges.length,
                  tone: "from-amber-500/35 to-orange-500/10 border-amber-200/45 text-amber-50",
                  labelTone: "text-amber-100/95",
                },
              ].map((stat) => (
                <div
                  key={stat.label}
                  style={{ minWidth: "88px" }}
                  className={`group relative flex min-w-[88px] flex-col items-center overflow-hidden rounded-xl border bg-gradient-to-br px-4 py-2.5 text-center shadow-[0_14px_24px_-18px_rgba(15,23,42,0.9)] backdrop-blur-sm transition hover:-translate-y-0.5 ${stat.tone}`}
                >
                  <span className="pointer-events-none absolute -right-3 -top-4 h-10 w-10 rounded-full bg-white/20 blur-xl" />
                  <span className="text-xl font-black leading-none">
                    {stat.value}
                  </span>
                  <span
                    className={`mt-1 text-[10px] uppercase tracking-[0.18em] ${stat.labelTone}`}
                  >
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Tabs + content ── */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {/* Tab bar */}
        <div className="flex items-center overflow-x-auto border-b border-slate-200 px-2">
          {tabs.map((tab) => {
            const isActive = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                aria-selected={isActive}
                className={`relative flex shrink-0 items-center gap-1 px-3 py-3.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-orange-600"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {isActive && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t-full bg-orange-500" />
                )}
                {tab.label}
                {tab.count !== undefined && (
                  <span
                    className={`rounded-full px-1 py-0.5 text-[11px] font-semibold leading-none ${
                      isActive
                        ? "text-orange-700"
                        : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    ({tab.count})
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="p-5 sm:p-6">
          {/* ── OVERVIEW ── */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* KPI strip */}
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-xl border border-emerald-100 bg-emerald-50/80 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-emerald-600">
                    Leads · último mês
                  </p>
                  <p className="mt-1 text-3xl font-black text-emerald-700">
                    {latestLeadCount}
                  </p>
                  <p className="text-xs text-emerald-500">
                    Total {trendMonths}m: {totalLeads}
                  </p>
                </div>
                <div className="rounded-xl border border-blue-100 bg-blue-50/80 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-blue-600">
                    Orçamentos · último mês
                  </p>
                  <p className="mt-1 text-3xl font-black text-blue-700">
                    {latestBudgetCount}
                  </p>
                  <p className="text-xs text-blue-500">
                    Total {trendMonths}m: {totalBudgets}
                  </p>
                </div>
                <div className="rounded-xl border border-violet-100 bg-violet-50/80 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-violet-600">
                    Usuários ativos
                  </p>
                  <p className="mt-1 text-3xl font-black text-violet-700">
                    {users.filter((u) => u.isActive).length}
                  </p>
                  <p className="text-xs text-violet-500">
                    De {users.length} cadastrados
                  </p>
                </div>
                <div className="rounded-xl border border-amber-100 bg-amber-50/80 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-600">
                    Licenças ativas
                  </p>
                  <p className="mt-1 text-3xl font-black text-amber-700">
                    {subscription?.activeLicenses ?? "—"}
                  </p>
                  <p className="text-xs text-amber-500">
                    Limite: {subscription?.licenseLimit ?? "—"}
                  </p>
                </div>
              </div>

              {/* Main grid */}
              <div className="grid gap-5 lg:grid-cols-3">
                {/* Corporate profile */}
                <div className="lg:col-span-2 space-y-3">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-slate-700">
                      Perfil corporativo
                    </h2>
                    <span className="text-[10px] uppercase tracking-widest text-slate-400">
                      Dados oficiais
                    </span>
                  </div>
                  <div className="grid gap-2.5 sm:grid-cols-2">
                    {detailRows.map((row) => {
                      const isEditable = editableFieldKeys.has(row.key);
                      return (
                        <div
                          key={row.label}
                          className="group relative rounded-xl border border-slate-200 bg-slate-50/80 p-3 transition hover:border-slate-200 hover:bg-white hover:shadow-sm"
                        >
                          {isEditable && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleOpenCompanyEditModal(row.key);
                              }}
                              className="absolute right-2.5 top-2.5 rounded-lg border border-slate-200 bg-white p-1 text-slate-400 opacity-0 shadow-sm transition group-hover:opacity-100 hover:text-orange-600"
                              aria-label={`Editar ${row.label}`}
                            >
                              <HiPencilAlt className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <p className="text-[10px] font-medium uppercase tracking-widest text-slate-400">
                            {row.label}
                          </p>
                          <p className="mt-0.5 text-sm font-semibold leading-snug text-slate-800">
                            {row.value}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Subscription sidebar */}
                <div className="space-y-3">
                  <h2 className="text-sm font-semibold text-slate-700">
                    Plano e assinatura
                  </h2>

                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-5">
                    <p className="text-[10px] font-medium uppercase tracking-widest text-slate-400">
                      Plano ativo
                    </p>
                    <p className="mt-1 text-2xl font-bold text-slate-900">
                      {planName}
                    </p>
                    <p className="text-sm text-slate-500">{planCycleLabel}</p>

                    <div className="mt-4 divide-y divide-slate-100">
                      <div className="flex items-center justify-between py-2.5">
                        <span className="text-xs text-slate-500">
                          Expiração
                        </span>
                        <span className="text-xs font-semibold text-slate-800">
                          {formatDate(planExpiration)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-2.5">
                        <span className="text-xs text-slate-500">Licenças</span>
                        <span className="text-xs font-semibold text-slate-800">
                          {subscription?.activeLicenses ?? "—"} /{" "}
                          {subscription?.licenseLimit ?? "—"}
                        </span>
                      </div>
                      {subscription?.planValue && (
                        <div className="flex items-center justify-between py-2.5">
                          <span className="text-xs text-slate-500">
                            Próx. pagamento
                          </span>
                          <span className="text-xs font-semibold text-slate-800">
                            {formatCurrency(subscription.planValue)}
                          </span>
                        </div>
                      )}
                      {company?.billingPortalUrl && (
                        <div className="pt-3">
                          <a
                            href={company.billingPortalUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs font-medium text-orange-600 hover:underline"
                          >
                            Acessar portal de cobrança →
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                    <p className="mb-3 text-[10px] font-medium uppercase tracking-widest text-slate-400">
                      Limites do plano
                    </p>
                    <div className="space-y-2.5">
                      {[
                        {
                          label: "Leads / mês",
                          value: company?.leadGeneratorMonthlyLimit,
                        },
                        {
                          label: "Orçamentos / mês",
                          value: company?.budgetLimit,
                        },
                        { label: "Funis", value: company?.funnelLimit },
                      ].map((limit) => (
                        <div
                          key={limit.label}
                          className="flex items-center justify-between"
                        >
                          <span className="text-xs text-slate-500">
                            {limit.label}
                          </span>
                          <span className="text-sm font-bold text-slate-800">
                            {limit.value !== undefined && limit.value !== null
                              ? limit.value
                              : "—"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── INTEGRATIONS ── */}
          {activeTab === "integrations" && (
            <div className="space-y-4">
              {(integrationAccessState.error || integrationAccessError) && (
                <Alert color="failure" className="text-sm">
                  {integrationAccessState.error ||
                    "Não foi possível carregar as permissões de integrações."}
                </Alert>
              )}

              {isLoadingIntegrationAccess ? (
                <div className="flex items-center gap-2 py-2 text-sm text-slate-500">
                  <Spinner size="sm" />
                  Carregando permissões...
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/80">
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Integração
                        </th>
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Escopo
                        </th>
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Status
                        </th>
                        <th className="px-4 py-3 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Ação
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {integrationAccessState.integrations.map(
                        (integration) => {
                          const key = String(integration?.key || "");
                          const isEnabled = Boolean(
                            integrationAccessState.accessMap?.[key],
                          );
                          const isSaving =
                            integrationAccessState.savingKey === key;
                          const scopeLabel =
                            String(integration?.scope || "").toLowerCase() ===
                            "user"
                              ? "Usuário"
                              : "Empresa";

                          return (
                            <tr
                              key={key}
                              className="transition-colors hover:bg-slate-50/70"
                            >
                              <td className="px-4 py-3">
                                <p className="font-semibold text-slate-800">
                                  {integration?.label || key}
                                </p>
                                <p className="text-xs text-slate-400">{key}</p>
                              </td>
                              <td className="px-4 py-3 text-slate-500">
                                {scopeLabel}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                    isEnabled
                                      ? "bg-emerald-100 text-emerald-700"
                                      : "bg-slate-200 text-slate-600"
                                  }`}
                                >
                                  {isEnabled ? "Ativada" : "Desativada"}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right flex justify-end">
                                <Button
                                  size="xs"
                                  color={isEnabled ? "failure" : "success"}
                                  onClick={() =>
                                    handleIntegrationToggle(key, !isEnabled)
                                  }
                                  disabled={isSaving}
                                >
                                  {isSaving ? (
                                    <span className="inline-flex items-center gap-2">
                                      <Spinner size="sm" />
                                      Salvando
                                    </span>
                                  ) : isEnabled ? (
                                    "Desativar"
                                  ) : (
                                    "Ativar"
                                  )}
                                </Button>
                              </td>
                            </tr>
                          );
                        },
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {integrationConfigState.error && (
                <Alert color="failure" className="text-sm">
                  {integrationConfigState.error}
                </Alert>
              )}
              {integrationConfigState.success && (
                <Alert color="success" className="text-sm">
                  {integrationConfigState.success}
                </Alert>
              )}

              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="mb-3">
                  <h3 className="text-sm font-semibold text-slate-700">
                    Variáveis de configuração
                  </h3>
                  <p className="text-xs text-slate-500">
                    Configure parâmetros somente para integrações ativas.
                  </p>
                </div>

                <div className="space-y-4">
                  {integrationAccessState.integrations
                    .filter(
                      (integration) =>
                        Boolean(
                          integrationAccessState.accessMap?.[
                            String(integration?.key || "")
                          ],
                        ) &&
                        Boolean(
                          INTEGRATION_CONFIG_SPECS[
                            String(integration?.key || "")
                          ] ||
                            INTEGRATION_CONFIG_NOTES[
                              String(integration?.key || "")
                            ],
                        ),
                    )
                    .map((integration) => {
                      const key = String(integration?.key || "");
                      const spec = INTEGRATION_CONFIG_SPECS[key] || null;
                      const note = INTEGRATION_CONFIG_NOTES[key] || "";

                      if (!spec) {
                        return (
                          <div
                            key={key}
                            className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3"
                          >
                            <p className="text-sm font-semibold text-slate-800">
                              {integration?.label || key}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">{note}</p>
                          </div>
                        );
                      }

                      const isSaving =
                        integrationConfigState.savingIntegrationKey === key;

                      return (
                        <div
                          key={key}
                          className="rounded-xl border border-slate-200 bg-slate-50/70 p-4"
                        >
                          <div className="mb-3">
                            <p className="text-sm font-semibold text-slate-800">
                              {spec.title}
                            </p>
                            <p className="text-xs text-slate-500">{spec.description}</p>
                          </div>

                          <div className="grid gap-3 md:grid-cols-2">
                            {spec.fields.map((field) => (
                              <div
                                key={`${key}-${field.key}`}
                                className={field.type === "textarea" ? "md:col-span-2" : ""}
                              >
                                <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                                  {field.label}
                                </p>
                                {field.type === "textarea" ? (
                                  <Textarea
                                    rows={4}
                                    value={integrationConfigState.form?.[field.key] ?? ""}
                                    onChange={(e) =>
                                      handleIntegrationConfigChange(
                                        field.key,
                                        e.target.value,
                                      )
                                    }
                                  />
                                ) : (
                                  <TextInput
                                    type={field.type || "text"}
                                    autoComplete="off"
                                    value={integrationConfigState.form?.[field.key] ?? ""}
                                    onChange={(e) =>
                                      handleIntegrationConfigChange(
                                        field.key,
                                        e.target.value,
                                      )
                                    }
                                  />
                                )}
                              </div>
                            ))}
                          </div>

                          <div className="mt-3 flex justify-end">
                            <Button
                              size="xs"
                              color="success"
                              onClick={() => handleSaveIntegrationConfig(key)}
                              disabled={isSaving}
                            >
                              {isSaving ? (
                                <span className="inline-flex items-center gap-2">
                                  <Spinner size="sm" />
                                  Salvando
                                </span>
                              ) : (
                                "Salvar variáveis"
                              )}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          {/* ── INDICATORS ── */}
          {activeTab === "indicators" && (
            <div className="space-y-5">
              <div>
                <h2 className="text-sm font-semibold text-slate-700">
                  Indicadores mensais
                </h2>
                <p className="text-xs text-slate-500">
                  Leads e orçamentos gerados nos últimos {trendMonths} meses
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-xl border border-emerald-100 bg-emerald-50/80 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-emerald-600">
                    Leads · último mês
                  </p>
                  <p className="mt-1 text-3xl font-black text-emerald-700">
                    {latestLeadCount}
                  </p>
                </div>
                <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-emerald-600">
                    Total de leads
                  </p>
                  <p className="mt-1 text-3xl font-black text-emerald-700">
                    {totalLeads}
                  </p>
                </div>
                <div className="rounded-xl border border-blue-100 bg-blue-50/80 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-blue-600">
                    Orçamentos · último mês
                  </p>
                  <p className="mt-1 text-3xl font-black text-blue-700">
                    {latestBudgetCount}
                  </p>
                </div>
                <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-blue-600">
                    Total de orçamentos
                  </p>
                  <p className="mt-1 text-3xl font-black text-blue-700">
                    {totalBudgets}
                  </p>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-5">
                  <div className="mb-4 flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-700">
                        Leads gerados
                      </p>
                      <p className="text-xs text-slate-500">Evolução mensal</p>
                    </div>
                    <span className="text-2xl font-bold text-slate-900">
                      {latestLeadCount}
                    </span>
                  </div>
                  <Chart
                    options={leadChartOptions}
                    series={[{ name: "Leads", data: leadTrendData.values }]}
                    type="area"
                    height={220}
                  />
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-5">
                  <div className="mb-4 flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-700">
                        Orçamentos gerados
                      </p>
                      <p className="text-xs text-slate-500">Evolução mensal</p>
                    </div>
                    <span className="text-2xl font-bold text-slate-900">
                      {latestBudgetCount}
                    </span>
                  </div>
                  <Chart
                    options={budgetChartOptions}
                    series={[
                      { name: "Orçamentos", data: budgetTrendData.values },
                    ]}
                    type="area"
                    height={220}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── USERS ── */}
          {activeTab === "users" && (
            <div className="space-y-4">
              <div>
                <h2 className="text-sm font-semibold text-slate-700">
                  Usuários
                </h2>
                <p className="text-xs text-slate-500">
                  {users.length} cadastrados
                </p>
              </div>
              {impersonateState.error && (
                <Alert color="failure" className="text-sm">
                  {impersonateState.error}
                </Alert>
              )}
              {users.length === 0 ? (
                <div className="flex items-center justify-center py-12 text-sm text-slate-400">
                  Nenhum usuário cadastrado.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/80">
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Usuário
                        </th>
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Permissão
                        </th>
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Status
                        </th>
                        <th className="px-4 py-3 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {users.map((user) => (
                        <tr
                          key={user.id}
                          className="transition-colors hover:bg-slate-50/70"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-600">
                                {(user.name || "?").charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-semibold text-slate-800">
                                  {user.name}
                                </p>
                                <p className="text-xs text-slate-400">
                                  {user.email}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {PERMISSION_LABELS[user.permissionId] ||
                              user.permissionId ||
                              "—"}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                user.isActive
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-slate-100 text-slate-500"
                              }`}
                            >
                              {user.isActive ? "Ativo" : "Inativo"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                disabled={
                                  impersonateState.loadingId === user.id
                                }
                                onClick={() => handleImpersonateUser(user)}
                                title="Personificar"
                                className="rounded-lg border border-slate-200 bg-white p-1.5 text-amber-600 shadow-sm transition hover:border-amber-300 hover:bg-amber-50 disabled:opacity-50"
                              >
                                <HiUser className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => openUserEditModal(user)}
                                title="Editar"
                                className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600"
                              >
                                <HiPencilAlt className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => openUserDeleteModal(user)}
                                title="Excluir"
                                className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 shadow-sm transition hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                              >
                                <HiOutlineTrash className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── FUNNELS ── */}
          {activeTab === "funnels" && (
            <div className="space-y-4">
              <div>
                <h2 className="text-sm font-semibold text-slate-700">Funis</h2>
                <p className="text-xs text-slate-500">{funnels.length} total</p>
              </div>
              {funnels.length === 0 ? (
                <div className="flex items-center justify-center py-12 text-sm text-slate-400">
                  Nenhum funil cadastrado.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/80">
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Nome
                        </th>
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Time responsável
                        </th>
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Visibilidade
                        </th>
                        <th className="px-4 py-3 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {funnels.map((funnel) => (
                        <tr
                          key={funnel.id}
                          className="transition-colors hover:bg-slate-50/70"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-xs font-bold text-orange-600">
                                {(funnel.name || "F").charAt(0).toUpperCase()}
                              </div>
                              <span className="font-semibold text-slate-800">
                                {funnel.name}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {funnel.team?.name || "—"}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {funnel.visibility || "—"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => handleShowFunnelDetails(funnel)}
                              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700"
                            >
                              Abrir
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── TEAMS ── */}
          {activeTab === "teams" && (
            <div className="space-y-4">
              <div>
                <h2 className="text-sm font-semibold text-slate-700">Times</h2>
                <p className="text-xs text-slate-500">
                  {teams.length} vinculados
                </p>
              </div>
              {teams.length === 0 ? (
                <div className="flex items-center justify-center py-12 text-sm text-slate-400">
                  Nenhum time vinculado.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full min-w-[480px] text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/80">
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Time
                        </th>
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Status
                        </th>
                        <th className="px-4 py-3 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {teams.map((team) => (
                        <tr
                          key={team.id}
                          className="transition-colors hover:bg-slate-50/70"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-xs font-bold text-orange-600">
                                {(team.name || "T").charAt(0).toUpperCase()}
                              </div>
                              <span className="font-semibold text-slate-800">
                                {team.name}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                team.status === 1
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-slate-100 text-slate-500"
                              }`}
                            >
                              {team.status === 1 ? "Ativo" : "Inativo"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => handleShowTeamMembers(team)}
                                className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
                              >
                                Membros
                              </button>
                              <button
                                onClick={() => openTeamEditModal(team)}
                                title="Editar"
                                className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600"
                              >
                                <HiPencilAlt className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => openTeamDeleteModal(team)}
                                title="Excluir"
                                className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 shadow-sm transition hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                              >
                                <HiOutlineTrash className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── FILIAIS ── */}
          {activeTab === "filiais" && (
            <div className="space-y-4">
              <div>
                <h2 className="text-sm font-semibold text-slate-700">
                  Filiais
                </h2>
                <p className="text-xs text-slate-500">
                  {filiais.length} cadastradas
                </p>
              </div>
              {filiais.length === 0 ? (
                <div className="flex items-center justify-center py-12 text-sm text-slate-400">
                  Nenhuma filial vinculada a esta empresa.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full min-w-[740px] text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/80">
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Nome
                        </th>
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Documento
                        </th>
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          E-mail
                        </th>
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Status
                        </th>
                        <th className="px-4 py-3 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {filiais.map((filial) => (
                        <tr
                          key={filial.id}
                          className="transition-colors hover:bg-slate-50/70"
                        >
                          <td className="px-4 py-3 font-semibold text-slate-800">
                            {filial.socialReason ||
                              filial.name ||
                              `Filial ${filial.id}`}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {filial.document || "—"}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {filial.email || "—"}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                filial.status === 1
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-slate-100 text-slate-500"
                              }`}
                            >
                              {filial.status === 1 ? "Ativa" : "Inativa"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Link
                              to={`/companies/${filial.id}`}
                              className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700"
                            >
                              Abrir
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── PAYMENTS ── */}
          {activeTab === "payments" && (
            <div className="space-y-4">
              <div>
                <h2 className="text-sm font-semibold text-slate-700">
                  Pagamentos
                </h2>
                <p className="text-xs text-slate-500">
                  {charges.length} lançamentos
                </p>
              </div>
              {charges.length === 0 ? (
                <div className="flex items-center justify-center py-12 text-sm text-slate-400">
                  Nenhuma cobrança encontrada.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/80">
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Descrição
                        </th>
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Vencimento
                        </th>
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Valor
                        </th>
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {charges.map((charge) => {
                        const sc = badgeColorFromStatus(charge.status);
                        return (
                          <tr
                            key={
                              charge.id ||
                              `${charge.externalReference}-${charge.dueDate}`
                            }
                            className="transition-colors hover:bg-slate-50/70"
                          >
                            <td className="px-4 py-3 font-medium text-slate-800">
                              {charge.description ||
                                charge.externalReference ||
                                "Cobrança"}
                            </td>
                            <td className="px-4 py-3 text-slate-500">
                              {formatDate(charge.dueDate)}
                            </td>
                            <td className="px-4 py-3 font-semibold text-slate-800">
                              {formatCurrency(charge.value)}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                  sc === "success"
                                    ? "bg-emerald-100 text-emerald-700"
                                    : sc === "failure"
                                      ? "bg-red-100 text-red-700"
                                      : "bg-amber-100 text-amber-700"
                                }`}
                              >
                                {charge.status || "—"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── MODALS ── */}

      {/* Company edit */}
      <Modal
        show={companyEditModal.open}
        size="4xl"
        onClose={closeCompanyEditModal}
      >
        <Modal.Header>Editar informações da empresa</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            {companyEditModal.error && (
              <Alert color="failure">{companyEditModal.error}</Alert>
            )}
            <div className="grid gap-4 md:grid-cols-2">
              {EDITABLE_FIELDS.map((field) => (
                <div key={field.key}>
                  <p className="mb-1 text-xs uppercase tracking-[0.4em] text-slate-500">
                    {field.label}
                  </p>
                  <TextInput
                    placeholder={field.placeholder}
                    value={companyForm[field.key] ?? ""}
                    onChange={(e) =>
                      handleCompanyFormChange(field.key, e.target.value)
                    }
                    autoFocus={companyEditModal.focusField === field.key}
                    disabled={companyEditModal.loading}
                  />
                </div>
              ))}
            </div>
            <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
              <p className="text-xs uppercase tracking-[0.4em] text-slate-500">
                Plano e assinatura
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="mb-1 text-[0.6rem] uppercase tracking-[0.4em] text-slate-400">
                    Plano
                  </p>
                  <Select
                    value={companyForm.planId || ""}
                    onChange={(e) => handlePlanChange(e.target.value)}
                    disabled={companyEditModal.loading || companyForm.isTrial}
                  >
                    <option value="">Selecione um plano</option>
                    {planOptions.map((plan) => (
                      <option key={plan.id} value={String(plan.id)}>
                        {plan.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <p className="mb-1 text-[0.6rem] uppercase tracking-[0.4em] text-slate-400">
                    Ciclo
                  </p>
                  <Select
                    value={companyForm.planCycleId || ""}
                    onChange={(e) => handleCycleChange(e.target.value)}
                    disabled={
                      companyEditModal.loading ||
                      companyForm.isTrial ||
                      !selectedPlan
                    }
                  >
                    <option value="">
                      {selectedPlan
                        ? "Selecione um ciclo"
                        : "Selecione um plano"}
                    </option>
                    {planCycles.map((cycle) => (
                      <option key={cycle.id} value={String(cycle.id)}>
                        {`${cycle.intervalMonths} meses • ${formatCurrency(cycle.price)}`}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              <div>
                <p className="mb-1 text-[0.6rem] uppercase tracking-[0.4em] text-slate-400">
                  Expiração
                </p>
                <TextInput
                  type="date"
                  value={companyForm.planExpirationDate ?? ""}
                  onChange={(e) =>
                    handleCompanyFormChange(
                      "planExpirationDate",
                      e.target.value,
                    )
                  }
                  disabled={companyEditModal.loading}
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  id="trial-toggle"
                  type="checkbox"
                  checked={Boolean(companyForm.isTrial)}
                  onChange={(e) => handleTrialToggle(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-slate-700"
                />
                <label
                  htmlFor="trial-toggle"
                  className="text-sm font-semibold text-slate-700"
                >
                  Marcar como período de teste
                </label>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="success"
            onClick={handleSaveCompanyInfo}
            disabled={companyEditModal.loading}
          >
            {companyEditModal.loading ? <Spinner size="sm" /> : "Salvar"}
          </Button>
          <Button color="light" onClick={closeCompanyEditModal}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Team members */}
      <Modal
        show={teamMembersModal.isOpen}
        size="md"
        onClose={closeTeamMembers}
      >
        <Modal.Header>
          Membros de {teamMembersModal.team?.name || "time"}
        </Modal.Header>
        <Modal.Body>
          {teamMembersModal.loading ? (
            <div className="flex items-center gap-2">
              <Spinner size="sm" />
              Carregando membros...
            </div>
          ) : teamMembersModal.error ? (
            <p className="text-sm text-red-600">{teamMembersModal.error}</p>
          ) : !teamMembersModal.members.length ? (
            <p className="text-sm text-slate-600">
              Nenhum membro listado para este time.
            </p>
          ) : (
            <ul className="space-y-3">
              {teamMembersModal.members.map((member) => (
                <li
                  key={member.id || member.userId || member.email}
                  className="flex items-center gap-3"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-600">
                    {(
                      member.name ||
                      member.fullName ||
                      member.user?.name ||
                      "?"
                    )
                      .charAt(0)
                      .toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-700">
                      {member.name ||
                        member.fullName ||
                        member.user?.name ||
                        "Usuário"}
                    </p>
                    <p className="text-xs text-slate-500">
                      {member.email || member.user?.email || "—"}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button color="light" onClick={closeTeamMembers}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Team edit */}
      <Modal show={teamEditModal.open} size="md" onClose={closeTeamEditModal}>
        <Modal.Header>Editar time</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            {teamEditModal.error && (
              <p className="text-sm text-red-600">{teamEditModal.error}</p>
            )}
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.4em] text-slate-500">
                Nome
              </p>
              <TextInput
                placeholder="Nome do time"
                value={teamEditModal.form.name}
                onChange={(e) =>
                  setTeamEditModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, name: e.target.value },
                  }))
                }
              />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.4em] text-slate-500">
                Descrição
              </p>
              <Textarea
                placeholder="Descrição curta do escopo"
                value={teamEditModal.form.description}
                onChange={(e) =>
                  setTeamEditModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, description: e.target.value },
                  }))
                }
                rows={3}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="success"
            onClick={saveTeamChanges}
            disabled={teamEditModal.loading}
          >
            {teamEditModal.loading ? <Spinner size="sm" /> : "Salvar"}
          </Button>
          <Button color="light" onClick={closeTeamEditModal}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Team delete */}
      <Modal
        show={teamDeleteModal.open}
        size="md"
        onClose={closeTeamDeleteModal}
      >
        <Modal.Header>Excluir time</Modal.Header>
        <Modal.Body>
          <p className="text-sm text-slate-600">
            Tem certeza que deseja excluir o time{" "}
            <strong>{teamDeleteModal.team?.name}</strong>? Esta ação não pode
            ser revertida, mas você pode recriar o time posteriormente.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="failure"
            onClick={handleDeleteTeam}
            disabled={teamDeleteModal.loading}
          >
            {teamDeleteModal.loading ? <Spinner size="sm" /> : "Excluir"}
          </Button>
          <Button color="light" onClick={closeTeamDeleteModal}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* User edit */}
      <Modal show={userEditModal.open} size="md" onClose={closeUserEditModal}>
        <Modal.Header>Editar usuário</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            {userEditModal.error && (
              <p className="text-sm text-red-600">{userEditModal.error}</p>
            )}
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.4em] text-slate-500">
                Nome
              </p>
              <TextInput
                placeholder="Nome completo"
                value={userEditModal.form.name}
                onChange={(e) =>
                  setUserEditModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, name: e.target.value },
                  }))
                }
              />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.4em] text-slate-500">
                E-mail
              </p>
              <TextInput
                placeholder="usuario@empresa.com"
                value={userEditModal.form.email}
                onChange={(e) =>
                  setUserEditModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, email: e.target.value },
                  }))
                }
              />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.4em] text-slate-500">
                Permissão
              </p>
              <Select
                value={userEditModal.form.permission}
                onChange={(e) =>
                  setUserEditModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, permission: e.target.value },
                  }))
                }
              >
                <option value="">Selecione a permissão</option>
                {PERMISSION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="success"
            onClick={saveUserChanges}
            disabled={userEditModal.loading}
          >
            {userEditModal.loading ? <Spinner size="sm" /> : "Salvar"}
          </Button>
          <Button color="light" onClick={closeUserEditModal}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* User delete */}
      <Modal
        show={userDeleteModal.open}
        size="md"
        onClose={closeUserDeleteModal}
      >
        <Modal.Header>Excluir usuário</Modal.Header>
        <Modal.Body>
          <p className="text-sm text-slate-600">
            Deseja remover o usuário{" "}
            <strong>{userDeleteModal.user?.name}</strong>? Você pode recriar o
            cadastro no futuro se precisar.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="failure"
            onClick={handleDeleteUser}
            disabled={userDeleteModal.loading}
          >
            {userDeleteModal.loading ? <Spinner size="sm" /> : "Excluir"}
          </Button>
          <Button color="light" onClick={closeUserDeleteModal}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Funnel detail */}
      <Modal show={funnelModal.open} size="5xl" onClose={closeFunnelModal}>
        <Modal.Header className="py-2">
          <div className="mt-1 text-sm md:text-base">Insights do funil</div>
        </Modal.Header>
        <Modal.Body className="pt-2">
          {funnelModal.loading ? (
            <div className="flex items-center gap-2">
              <Spinner size="sm" />
              Carregando detalhes...
            </div>
          ) : funnelModal.error ? (
            <p className="text-sm text-red-600">{funnelModal.error}</p>
          ) : (
            <>
              <div className="rounded-2xl border border-slate-200 bg-orange-500 px-4 py-4 text-white">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-[0.4em] text-white">
                      Funil
                    </p>
                    <p className="text-xl font-semibold">
                      {funnelModal.detail?.name || funnelModal.funnel?.name}
                    </p>
                    <p className="text-sm text-slate-300">
                      {funnelModal.detail?.description ||
                        funnelModal.funnel?.description ||
                        "Descrição não informada."}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      color={funnelModal.detail?.status ? "success" : "gray"}
                      className="uppercase tracking-[0.3em]"
                    >
                      {funnelModal.detail?.status ||
                        funnelModal.funnel?.status ||
                        "—"}
                    </Badge>
                    <span className="text-xs uppercase tracking-[0.35em] text-white">
                      {funnelOwner || "Sem responsável"}
                    </span>
                  </div>
                </div>
                <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                  {[
                    { label: "Visibilidade", value: funnelVisibility },
                    { label: "Etapas", value: stageSummaries.length },
                    { label: "Cards", value: totalCardsInFunnel },
                    { label: "Automações", value: totalAutomations },
                  ].map((s) => (
                    <div
                      key={s.label}
                      className="rounded-xl border border-white/10 bg-white/10 px-3 py-2"
                    >
                      <p className="text-xs uppercase tracking-[0.4em] text-white">
                        {s.label}
                      </p>
                      <p className="text-base font-semibold">{s.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5 grid gap-4 lg:grid-cols-[1.2fr,0.8fr]">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-600">
                      Estágios do funil
                    </p>
                    <span className="text-xs uppercase tracking-[0.4em] text-slate-400">
                      {stageSummaries.length} etapas
                    </span>
                  </div>
                  {stageSummaries.length ? (
                    <div className="mt-3 space-y-2">
                      {stageSummaries.map((stage) => (
                        <div
                          key={stage.id}
                          className="rounded-xl border border-slate-200 bg-slate-50/90 p-3"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-semibold text-slate-900">
                              {stage.order}. {stage.name}
                            </p>
                            <span className="text-[0.6rem] uppercase tracking-[0.4em] text-slate-500">
                              {stage.status}
                            </span>
                          </div>
                          <div className="mt-2 grid gap-2 text-xs text-slate-500 sm:grid-cols-3">
                            <span>
                              Cards: <strong>{stage.cardsCount}</strong>
                            </span>
                            <span>
                              Automações:{" "}
                              <strong>{stage.automationCount}</strong>
                            </span>
                            <span>
                              Ordem: <strong>{stage.order}</strong>
                            </span>
                          </div>
                          {stage.responsible && (
                            <p className="mt-2 text-xs text-slate-500">
                              Responsável: {stage.responsible}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-slate-600">
                      Nenhuma etapa definida para este funil.
                    </p>
                  )}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-600">
                      Automações
                    </p>
                    <span className="text-xs uppercase tracking-[0.4em] text-slate-400">
                      {totalAutomations} programada(s)
                    </span>
                  </div>
                  {totalAutomations ? (
                    <div className="mt-3 space-y-2">
                      {funnelModal.automations.map((automation) => {
                        const triggerSummary =
                          describeTriggerSummary(automation);
                        const actionsSummary =
                          describeActionsSummary(automation);
                        return (
                          <div
                            key={automation.id || automation.name}
                            className="rounded-xl border border-slate-200 bg-white/80 p-3"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-sm font-semibold text-slate-900">
                                {automation.name ||
                                  `Automação ${automation.id}`}
                              </p>
                              <Badge
                                color={automation.isActive ? "success" : "gray"}
                              >
                                {automation.isActive ? "Ativa" : "Inativa"}
                              </Badge>
                            </div>
                            {!!triggerSummary && (
                              <p className="mt-2 text-xs text-slate-500">
                                Gatilho: {triggerSummary}
                              </p>
                            )}
                            {!!actionsSummary && (
                              <p className="mt-1 text-xs text-slate-500">
                                Ações: {actionsSummary}
                              </p>
                            )}
                            {automation.targetStage?.name && (
                              <p className="mt-1 text-xs text-slate-500">
                                Destino: {automation.targetStage.name}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-slate-600">
                      Nenhuma automação cadastrada para este funil.
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="flex justify-end">
          <Button color="light" onClick={closeFunnelModal}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CompanyDetailPage;
